import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Script } from "components/ExtendedContent/ExtendedContentContainer/layout";
import { scriptLoader } from "util/scriptLoader";
import { useStore, StoreTypes } from "context";
import { parseChineseOnly } from "util/zhuyin";
import * as types from "constants/actionTypes";
import { PAGE_SIZE } from "./utils/constants";
import { splitIntoPagesString } from "./utils/pageSplitter";
import TextStructureHandler from "./TextStructureHandler";
import ContentStructureHandler from "./ContentStructureHandler";
import { EventBusType, EventBus } from 'events/EventBus';
import { CanvasEvent } from 'events/EventTypes';
import { LayoutType } from "components/ExtendedContent/ExtendedContentContainer/layout";
import { SplitType } from "constants/splitType";
import { adjustImagesToFitPage } from "./TextStructureHandler/display";
import { normalizeAnswerSectionsInHtml } from "util/topicStructure";
import {
  handleAnswerToggleClick,
  stripLayoutScriptAnswerListeners,
} from "util/answerToggle";
import classnames from "classnames";
import "./imageSet.scss";

/**
 * 圖片型人工切頁：判斷某一頁的 .abreast 裡到底有沒有「文字欄」。
 *
 * 人工標記工具匯出的 JSON 可能產生「只有圖片、沒有 .text」的頁（NBK-1668 的第一頁：
 * .abreast 的子元素是 [br, .img, br]，整份 .imageSet 裡 0 個 .text）。這種頁不該
 * 佔一個頁碼 —— 驗收標準要求人工切頁第一頁的圖文組合與程式切頁一致。
 *
 * 結構不符預期時一律回 true（保守：寧可留著也不要誤刪內容）。
 */
const imageSetPageHasText = (pageHtml) => {
  if (typeof pageHtml !== 'string') return true;
  const doc = new DOMParser().parseFromString(pageHtml, 'text/html');
  const abreastEl = doc.querySelector('.imageSet .abreast');
  if (!abreastEl) return true;
  const textEl = abreastEl.querySelector('.text');
  return !!textEl && textEl.textContent.trim() !== '';
};

/**
 * 圖片型的字級縮放：以「右欄文字欄」自己的溢出為準，只縮 .imageSet 的字級。
 *
 * 為什麼不能沿用通用的 content.scrollHeight 判斷：.text 的 overflow-x 不是 visible，
 * 依 CSS 規範另一軸的 visible 會被算成 auto，於是 .text 自己成為一個捲動容器 ——
 * 文字的溢出根本傳不到 content.scrollHeight。實測 content 對容器溢出 0px，
 * 但文字欄內部溢出 1306px，通用邏輯對此完全失明（NBK-1668）。
 *
 * 欄位高度交給 CSS：.noScroll .imageSet 是 column flex，標題佔自身高度、.abreast
 * 吃剩餘高度（NBK-1667）。標題字級是繼承來的，縮字時標題變矮、欄位會跟著變高，
 * 所以每一輪都要重新讀 clientHeight，不能只算一次。
 */
const IMAGE_SET_MIN_FONT_RATIO = 0.3;
const IMAGE_SET_SCALE_TOLERANCE = 5; // px
const IMAGE_SET_MAX_ITERATIONS = 20;

/**
 * 需要把圖片一併納入 adjustContentScale 縮放的型別。
 *
 * 這些型別的內容都是「文字 + 轉檔產生的固定像素圖片」混排（例如
 * <img width="476" height="94">），圖片不隨 fontSize 變化。螢幕顯示比例
 * 125% / 150% 會讓 CSS 視埠變小，圖片佔容器的比例因此變大並吃掉版面高度，
 * 只縮字級的話會一路觸底到 30% 仍然溢出，圖片以下的內容被 overflow:hidden
 * 靜默裁掉（NBK-1662 起於題目型，NBK-1671 擴及其餘文字結構型）。
 *
 * 刻意不含兩種型別：
 * - Image_108_2：adjustContentScale 一開始就走 isImageLayout 分支提早 return
 * - WordSection_108_2：直書型的圖片在 content.scss 已用 vmin 宣告
 *   （`.word_section .article .paragraph .content .img { width: 7vmin }` 等），
 *   本來就隨視埠縮放，再套一層等比縮放會重複縮小
 */
const IMG_SCALABLE_LAYOUTS = [
  LayoutType.Topic_108_2,
  LayoutType.PlainText,
  LayoutType.Fill_108_2,
  LayoutType.Simple_108_2,
];

const scaleImageSetTextColumn = (content) => {
  const imageSetEl = content.querySelector('.imageSet');
  if (!imageSetEl) return;
  const abreastEl = imageSetEl.querySelector('.abreast');
  if (!abreastEl) return;

  const textColumnEl = Array.from(abreastEl.children).find((el) => el.classList.contains('text'));
  if (!textColumnEl) {
    // 沒有文字欄（理論上已被 manualPages 濾掉）：維持 CSS 預設字級
    imageSetEl.style.fontSize = '';
    return;
  }
  const textContentEl = textColumnEl.querySelector('.text') || textColumnEl;

  // 縮放對象是「右欄文字欄」，不是 .imageSet：.title 與左欄圖片都在 .imageSet
  // 底下，縮 .imageSet 會讓標題跟著內文一起縮，人工切頁每頁各自縮，標題就隨
  // 翻頁忽大忽小（多題小視窗實測同一份內容標題第 1 頁 99px、第 2 頁 37px）。
  // NBK-1667 註解列的第一個問題就是「標題與內文一起被縮」。
  const fitsAtCurrentFont = () =>
    textContentEl.scrollHeight <= textColumnEl.clientHeight + IMAGE_SET_SCALE_TOLERANCE;

  imageSetEl.style.fontSize = ''; // 清掉舊版本可能殘留的值
  textColumnEl.style.fontSize = '';
  void textColumnEl.offsetHeight;

  if (fitsAtCurrentFont()) return; // 塞得下就維持 CSS 預設字級

  const originalFontSize = parseFloat(window.getComputedStyle(textColumnEl).fontSize);
  if (!originalFontSize) return;

  let minFontSize = originalFontSize * IMAGE_SET_MIN_FONT_RATIO;
  let maxFontSize = originalFontSize;
  let bestFontSize = minFontSize;

  for (let i = 0; i < IMAGE_SET_MAX_ITERATIONS && maxFontSize - minFontSize > 0.5; i++) {
    const midFontSize = (minFontSize + maxFontSize) / 2;
    textColumnEl.style.fontSize = `${midFontSize}px`;
    void textColumnEl.offsetHeight;

    if (fitsAtCurrentFont()) {
      bestFontSize = midFontSize;
      minFontSize = midFontSize;
    } else {
      maxFontSize = midFontSize;
    }
  }

  textColumnEl.style.fontSize = `${bestFontSize}px`;
};

const StaticHtmlComponent = ({ html, extendedResourceStyle }) => {
  const [{ bookId, books }] = useStore(StoreTypes.books);
  const [{ shouldSplited, splitType, totalPages, currentPage }, extendedContentDispatch] = useStore(StoreTypes.extendedContent);
  const book = books.find((info) => info.bookId === bookId);
  const highGradeCheck = book.display_name.includes("國中國文");
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const containerHeightRef = useRef(0);
  const isAdjustingRef = useRef(false);
  const [pagedHtml, setPagedHtml] = useState(null);
  const prevPageRef = useRef(currentPage);

  // 更新顯示的頁面 (參考 SynopsisWindow 的做法)
  const updateDisplayPage = useCallback((pageIndex) => {
    if (!contentRef.current) return;
    
    // 判斷 DOM 結構類型並選擇對應的頁面元素選擇器
    const textElements = contentRef.current.querySelectorAll('.text');
    const contentElements = contentRef.current.querySelectorAll('div.content');
    
    if (textElements.length > 0) {
      // .text 結構：每個 .text 元素代表一頁
      textElements.forEach((pageEl, index) => {
        if (index === pageIndex) {
          pageEl.style.visibility = 'visible';
          pageEl.style.position = 'relative';
        } else {
          pageEl.style.visibility = 'hidden';
          pageEl.style.position = 'absolute';
        }
      });
    } else if (contentElements.length > 0) {
      // div.content 結構：每個 .content 元素代表一頁
      contentElements.forEach((pageEl, index) => {
        if (index === pageIndex) {
          pageEl.style.visibility = 'visible';
          pageEl.style.position = 'relative';
        } else {
          pageEl.style.visibility = 'hidden';
          pageEl.style.position = 'absolute';
        }
      });
    }
  }, []);

  // 人工切頁的頁面清單（依頁碼排序）。圖片型會濾掉沒有文字欄的空白頁，見 imageSetPageHasText。
  const manualPages = useMemo(() => {
    if (splitType !== SplitType.MANUAL_SPLIT) return null;
    if (!html || typeof html !== 'object') return null;

    const ordered = Object.keys(html)
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => normalizeAnswerSectionsInHtml(html[key]));

    if (extendedResourceStyle !== LayoutType.Image_108_2) return ordered;

    const withText = ordered.filter(imageSetPageHasText);
    // 全部都沒文字時不做任何過濾，避免整份內容消失
    return withText.length > 0 ? withText : ordered;
  }, [html, splitType, extendedResourceStyle]);

  // 圖片型版面完全由 image108-2.css 收斂，不參與 contentRef 的縮放／高度重置。
  // 用 prop 判斷而非查 DOM：resetContentScale 會在 pagedHtml 尚未 render 時就被呼叫，
  // 那時 contentRef 內還沒有 .imageSet，查 DOM 會漏判。
  const isImageLayout = extendedResourceStyle === LayoutType.Image_108_2;

  // 重置內容縮放到預設大小
  const resetContentScale = useCallback(() => {
    if (!contentRef.current) return;
    if (isImageLayout) return; // 清掉 inline height:100% 會讓 .imageSet 的百分比高度失去依據
    const content = contentRef.current;
    content.style.fontSize = '';
    content.style.transform = 'none';
    content.style.transformOrigin = '';
    content.style.height = '';
    content.style.width = '100%';
  }, [isImageLayout]);

  // 調整內容縮放，使內容能顯示在可見範圍內
  const adjustContentScale = useCallback(() => {
    if (!containerRef.current || !contentRef.current) return;
    if (isAdjustingRef.current) return;

    const container = containerRef.current;
    const content = contentRef.current;
    const containerHeight = container.clientHeight;
    if (containerHeight <= 0) return;
    containerHeightRef.current = containerHeight;

    // 圖片型（.imageSet）不做縮放：版面已由 image108-2.css 自行收斂
    // （.imageSet 為 column flex → 標題佔自身高度、.abreast 吃剩餘高度、
    //   左欄圖片 object-fit:contain、右欄每頁高度由分頁邏輯算好），
    // 沒有需要靠縮字才塞得下的內容。硬跑這段反而會出兩個問題（NBK-1667）：
    //   1. 標題與內文一起被縮，且只有標題所在的那一頁縮，各頁字級對不起來
    //   2. 下面的 content.style.height = '' 會清掉 contentRef 的 inline height:100%，
    //      讓 .imageSet 的 height:100% 失去依據，版面反而爆掉
    //
    // 但上述前提只對「程式切頁」成立 —— 每頁塞多少字是 paging.js 算出來的。
    // 人工切頁的每頁內容由編輯者在標記工具裡決定，沒有任何邏輯保證塞得下：
    // 實測單頁文字 1826px 擠在 505px 的欄位裡。而 .text 的 overflow 讓它自成
    // 捲動容器，溢出傳不到 content.scrollHeight，通用邏輯對此結構性失明，
    // 只能改量欄位本身（NBK-1668）。這裡只調 .imageSet 字級，不碰 contentRef，
    // 上面兩個顧慮都不受影響。
    if (isImageLayout) {
      if (splitType === SplitType.MANUAL_SPLIT) {
        scaleImageSetTextColumn(content);
      }
      content.style.visibility = ''; // 呼叫端會先設 hidden，這裡要負責還原
      return;
    }

    isAdjustingRef.current = true;

    content.style.fontSize = '';
    content.style.transform = 'none';
    content.style.transformOrigin = '';
    content.style.height = '';
    content.style.width = '100%';

    // .title 用 10vmin 等絕對值，不繼承父層 fontSize，需單獨處理
    const fixedEls = content.querySelectorAll('.title');
    fixedEls.forEach(el => { el.style.fontSize = ''; });

    // 圖片有內在尺寸，不隨 fontSize 變化。題目型的大附圖（例如示意圖）會讓
    // scrollHeight 永遠撐在容器之上，二分搜尋一路觸底到 30% 仍然溢出，而
    // .contentView.splited > div 是 overflow:hidden，於是圖片以下的選項／答案／
    // 解析被靜默裁掉（NBK-1662）。這裡把圖片一併納入縮放，讓搜尋能真正收斂。
    const imgCandidates = IMG_SCALABLE_LAYOUTS.includes(extendedResourceStyle)
      ? Array.from(content.querySelectorAll('img'))
      : [];
    const resetImgScale = (img) => {
      img.style.maxHeight = '';
      img.style.maxWidth = '';
      img.style.width = '';
      img.style.height = '';
      delete img.dataset.fitScaled;
    };
    const applyImgScale = (img, baseHeight, scale) => {
      if (!baseHeight) return;
      // maxHeight 搭配 width/height auto，瀏覽器會等比縮放，不會變形
      img.style.maxHeight = `${baseHeight * scale}px`;
      img.style.maxWidth = '100%';
      img.style.width = 'auto';
      img.style.height = 'auto';
      // 標記 maxHeight 已由縮放邏輯接管，避免「.text img 最大高度限制器」覆蓋它
      img.dataset.fitScaled = '1';
    };
    imgCandidates.forEach(resetImgScale);

    void container.offsetHeight;

    // 記錄 .title 原始計算字體大小
    const fixedBaseSizes = [];
    fixedEls.forEach(el => {
      fixedBaseSizes.push(parseFloat(window.getComputedStyle(el).fontSize));
    });

    // 只縮「大到足以造成溢出」的圖片。題目型的 div.img 裡同時放標籤圖
    // （例如 alt="歷屆試題" 的 93x19 badge）和真正的題目附圖（756x584 示意圖），
    // 兩者用 class 或出現順序都無法可靠區分。而縮圖的目的本來就是降低
    // scrollHeight——badge 對溢出幾乎沒有貢獻，縮它只會讓標籤看不清楚，
    // 所以用高度門檻篩掉小圖，讓它們維持原尺寸。
    const imgScaleMinHeight = Math.max(40, containerHeight * 0.1);
    const scalableImgs = [];
    const imgBaseHeights = [];
    imgCandidates.forEach((img) => {
      const baseHeight = img.offsetHeight || img.naturalHeight || 0;
      if (baseHeight <= imgScaleMinHeight) return;
      scalableImgs.push(img);
      imgBaseHeights.push(baseHeight);
    });

    let contentHeight = content.scrollHeight;

    if (contentHeight > containerHeight && contentHeight > 0) {
      // 取得原始字體大小
      const computedStyle = window.getComputedStyle(content);
      const originalFontSize = parseFloat(computedStyle.fontSize);

      let minFontSize = originalFontSize * 0.3;
      let maxFontSize = originalFontSize;
      const tolerance = 0;
      const maxIterations = 20;

      let bestFontSize = minFontSize;
      let iteration = 0;

      while (maxFontSize - minFontSize > 0.5 && iteration < maxIterations) {
        const midFontSize = (minFontSize + maxFontSize) / 2;
        const scale = midFontSize / originalFontSize;

        content.style.fontSize = `${midFontSize}px`;
        fixedEls.forEach((el, i) => {
          el.style.fontSize = `${fixedBaseSizes[i] * scale}px`;
        });
        scalableImgs.forEach((img, i) => {
          applyImgScale(img, imgBaseHeights[i], scale);
        });

        void content.offsetHeight;
        
        // 測量調整後的實際高度
        const actualHeight = content.scrollHeight;

        if (actualHeight <= containerHeight + tolerance) {
          // 高度合適，記錄這個字體大小，嘗試更大的值
          bestFontSize = midFontSize;
          minFontSize = midFontSize;
        } else {
          // 高度太大，需要更小的字體
          maxFontSize = midFontSize;
        }

        iteration++;
      }

      // 以同一個比例套用字級、.title 與圖片，並強制重排以便下一次量測
      const applyFontScale = (scale) => {
        content.style.fontSize = `${originalFontSize * scale}px`;
        fixedEls.forEach((el, i) => {
          el.style.fontSize = `${fixedBaseSizes[i] * scale}px`;
        });
        scalableImgs.forEach((img, i) => {
          applyImgScale(img, imgBaseHeights[i], scale);
        });
        void content.offsetHeight;
      };

      // 2% 安全邊距，避免邊界情況溢出
      const finalScale = (bestFontSize * 0.98) / originalFontSize;
      applyFontScale(finalScale);

      // 收尾保證（NBK-1671）：上面的二分搜尋下限是原始字級的 30%，若連 30% 都
      // 塞不下（例如單張附圖的高度本身就接近或超過容器），結果仍會溢出，而祖先
      // .contentView 是 overflow:hidden，超出的部分會被「靜默裁掉」——這正好同時
      // 違反驗收標準的「不得被裁切」，卻讓「不出現卷軸」看起來是達成的。
      // 兩條標準只能靠繼續縮小同時滿足，所以這裡在觸底時再往下降，
      // 直到塞得下或到達可讀性底線。
      const READABLE_MIN_RATIO = 0.1;
      const GUARD_MAX_ITERATIONS = 12;
      let guardScale = finalScale;
      let guardIteration = 0;

      while (
        content.scrollHeight > containerHeight &&
        guardScale > READABLE_MIN_RATIO &&
        guardIteration < GUARD_MAX_ITERATIONS
      ) {
        guardScale = Math.max(READABLE_MIN_RATIO, guardScale * 0.85);
        applyFontScale(guardScale);
        guardIteration++;
      }

      if (content.scrollHeight > containerHeight) {
        // 縮到可讀性底線仍塞不下，只能讓祖先裁切。這種頁屬於資料端的極端案例，
        // 印出來才追查得到是哪一本、哪個型別，不要讓它繼續靜默失敗。
        console.warn(
          '[StaticHtmlComponent] 內容縮至可讀性下限仍超出容器，將被裁切',
          {
            extendedResourceStyle,
            containerHeight,
            contentHeight: content.scrollHeight,
            scale: guardScale,
          }
        );
      }

    } else {
      // 內容未超過容器，保持預設字體大小
      content.style.fontSize = '';
      fixedEls.forEach(el => { el.style.fontSize = ''; });
      imgCandidates.forEach(resetImgScale);
    }

    content.style.visibility = '';
    isAdjustingRef.current = false;
    // deps 取兩側聯集：isImageLayout / splitType 供圖片型分支（NBK-1667、NBK-1668），
    // extendedResourceStyle 供題目型的圖片納入縮放（NBK-1662）。
  }, [isImageLayout, splitType, extendedResourceStyle]);

  // 筆記標記相關的狀態
  // const [{ currentPageIndex }, setState] = useSetState({
  //   currentPageIndex: 0
  // });

  // 移除 MathML annotation，避免 MTEF 編碼在舊版 Chromium 顯示為亂碼
  const stripMathAnnotations = (html) => {
    if (!html || typeof html !== 'string') return html;
    return html
      .replace(/<annotation-xml[^>]*>[\s\S]*?<\/annotation-xml>/gi, '')
      .replace(/<annotation[^>]*>[\s\S]*?<\/annotation>/gi, '');
  };

  // 國中國文 (highGradeCheck) 需移除注音標籤以縮小行距 (hruby line-height:1.8 → 預設)。
  // 各分支先取出單頁字串再傳入，確保 NO_SPLIT / MANUAL_SPLIT / AUTO_SPLIT 各模式行為都與 1.27.0 一致。
  const parseHighGrade = (value) =>
    highGradeCheck && typeof value === "string"
      ? parseChineseOnly(value, "pirls")
      : value;

  // 將腳本載入移到分頁完成後
  useEffect(() => {
    if (pagedHtml && contentRef.current && html) {
      // 等待 DOM 完全渲染後再載入腳本
      const timer = setTimeout(() => {
        const script = scriptLoader(Script[extendedResourceStyle]);
        if (!script) return;
        // script 載入後剝掉它綁在答案/解析節點上的 toggle listener，
        // 改由下方的委派 handler 當唯一 owner（NBK-1663）。
        script.addEventListener("load", () => {
          stripLayoutScriptAnswerListeners(contentRef.current);
        });
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [pagedHtml, extendedResourceStyle, html]);

  // 點答案/解析區展開內容（NBK-1663）。
  // layout script 是把 listener 直接綁在 #answer / #analyze 節點上，那些節點
  // 之後會被重建（pagedHtml 重設換掉 innerHTML、容器的 <MathJax.Text> remount、
  // MathJax typeset 改寫 DOM），listener 隨舊節點消失且沒有重綁機制，於是點了
  // 沒反應。改掛在穩定的祖先 contentRef 上做事件委派，內容重建幾次都仍有效。
  // 同一套做法見 SynopsisWindow/index.js:59-101。
  useEffect(() => {
    const el = contentRef.current;
    if (!el || !pagedHtml) return;

    const handler = (e) => {
      handleAnswerToggleClick(e);
    };

    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, [pagedHtml]);

  useEffect(() => {
    if (!shouldSplited) return;
    if (pagedHtml && currentPage >= 0) {
      // 如果頁面真的改變了，保存舊頁面的畫記
      if (prevPageRef.current !== currentPage && prevPageRef.current >= 0) {
        EventBus.emit({
          eventBusType: EventBusType.ExtendedContent,
          event: CanvasEvent.SplitedPageSaveCanvasEvent,
          payload: { 
            currentSplitedPageIndex: prevPageRef.current
          }
        });
      }
      
      // 載入新頁面的畫記
      EventBus.emit({
        eventBusType: EventBusType.ExtendedContent,
        event: CanvasEvent.SplitedPageImportJSONEvent,
        payload: { 
          currentSplitedPageIndex: currentPage
        }
      });
      
      // 更新 ref
      prevPageRef.current = currentPage;
    }
  }, [currentPage, pagedHtml, shouldSplited]);

  // 響應 currentPage 變化，切換顯示內容 (參考 SynopsisWindow 的做法)
  useEffect(() => {
    // NO_SPLIT 或未啟用分頁時不需要處理
    if (!shouldSplited || splitType === SplitType.NO_SPLIT) return;
    
    // MANUAL_SPLIT 模式：html 是物件格式 { '1': html1, '2': html2, ... }，直接更換 pagedHtml 內容
    if (splitType === SplitType.MANUAL_SPLIT) {
      const pageHtml = manualPages && manualPages[currentPage];
      if (pageHtml) {
        setPagedHtml(stripMathAnnotations(parseHighGrade(pageHtml)));
      }
      return;
    }

    // AUTO_SPLIT 模式：html 是字串，分頁後透過 visibility 切換顯示
    if (splitType === SplitType.AUTO_SPLIT && pagedHtml && contentRef.current && totalPages > 0 && currentPage >= 0) {
      updateDisplayPage(currentPage);
    }
  }, [html, currentPage, pagedHtml, totalPages, shouldSplited, splitType, highGradeCheck, updateDisplayPage, manualPages]);

  // 注意：這裡曾經有 revealStandaloneSections（NBK-1664），在人工切頁翻到
  // 「答案／解析獨立成頁」時對 #answer / #analyze 補上 .show。
  //
  // 但 NBK-1664 的需求是「解答／解析頁需先顯示標題框」——只要標題框，內容仍要
  // 使用者點過才展開。而 topic108-2.css 的 20260402 那組規則本來就做到這件事：
  //     .noScroll .topic #answer          { opacity: 1 }   整塊可見 → 標題看得到
  //     .noScroll .topic #answer > .ans   { opacity: 0 }   內容藏起來
  //     .noScroll .topic #answer.show > .ans { opacity: 1 } 點了才顯示
  // 補 .show 等於把最後一條提前觸發，翻到該頁就直接看到答案內容，超出需求。
  //
  // NBK-1664 當初會用 JS 繞，是因為那條 branch 上 20260402 那組規則被
  // 36f0cb4f3（bulk commit）刪掉了，標題根本不會顯示；該 commit 訊息也留有
  // 「尚未驗證：解析頁的 CSS 是否確實顯示」。本 branch 上那組規則存在
  // （topic108-2.css 226-240 行），JS 因此不只多餘而是會洩題，故移除。
  //
  // 若日後又出現「翻到答案頁看不到標題框」，先確認 20260402 那組規則還在，
  // 不要再用 JS 補 .show。


  // 分頁處理
  useEffect(() => {
    if (!html || !containerRef.current) return;

    // MANUAL_SPLIT：html 是 { '1': html1, ... } 物件，先取出單頁字串處理（比照 SynopsisWindow）
    if (shouldSplited && splitType === SplitType.MANUAL_SPLIT) {
      if (!manualPages || manualPages.length === 0) return;
      setPagedHtml(stripMathAnnotations(parseHighGrade(manualPages[0])));
      // 更新 store 中的總頁數
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
        payload: { totalPages: manualPages.length },
      });
      return;
    }

    // 到這裡 html 一定是字串，先移除注音一次供下方 NO_SPLIT / AUTO_SPLIT 共用
    // 一併正規化巢狀的 #answer / #analyze：涵蓋 NO_SPLIT 與 AUTO_SPLIT。
    // AUTO_SPLIT 之後還會經過 paging.js 的 handleTextStructure（也會呼叫同一支），
    // 但該操作是冪等的（第二次不會再搬動、回傳同一個字串參考）。
    const parsedHtml = normalizeAnswerSectionsInHtml(parseHighGrade(html));

    // 3/12 #NBK-1408 splitType為undefined時導致畫面出現錯誤，為日後可追蹤此問題所以先註解掉
    // splitType 為 undefined 時，視為不切頁
    // if (splitType === SplitType.undefined) {
    //   setPagedHtml(html);
    //   extendedContentDispatch({ type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES, payload: { totalPages: 1 } });
    //   return;
    // }

    // 不分頁 / NO_SPLIT：單頁顯示
    if (!shouldSplited || splitType === SplitType.NO_SPLIT) {
      setPagedHtml(stripMathAnnotations(parsedHtml));
      // 更新 store 中的總頁數為 1
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
        payload: { totalPages: 1 },
      });
      return;
    }

    // AUTO_SPLIT
    resetContentScale();

    const measureContainer = containerRef.current;
    const originalHtml = parsedHtml;

    let cancelled = false;
    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;

      const computedStyle = window.getComputedStyle(measureContainer);
      const lineHeight = parseFloat(computedStyle.lineHeight) || 
                        parseFloat(computedStyle.fontSize) * 1.5;
      
      const { htmlString, totalPages } = splitIntoPagesString(
        originalHtml, 
        lineHeight, 
        PAGE_SIZE,
        measureContainer,
        extendedResourceStyle
      );
      
      if (cancelled) return;
      setPagedHtml(htmlString);
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
        payload: { totalPages },
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [html, highGradeCheck, extendedContentDispatch, extendedResourceStyle, shouldSplited, splitType, resetContentScale, manualPages]);

  // 當頁面切換、內容變化時調整縮放
  // 1. 穩定性檢測：連續兩個 frame 取得相同 clientHeight 才執行初始縮放
  // 2. MutationObserver：MathJax、scriptLoader 等異步改變 DOM 導致溢出時重新縮放
  // 3. ResizeObserver：容器尺寸變化（父層動畫/resize）時重新縮放
  useEffect(() => {
    if (!pagedHtml || !containerRef.current || !contentRef.current || !shouldSplited) return;

    let cancelled = false;
    let debounceId = null;
    let recheckId = null;
    const content = contentRef.current;

    // 隱藏內容直到縮放完成，防止未縮放內容閃爍
    content.style.visibility = 'hidden';

    // 穩定性檢測：等待佈局穩定後執行初始縮放
    let prevHeight = -1;
    let retries = 0;
    const MAX_RETRIES = 30;

    const waitForStableLayout = () => {
      if (cancelled) return;
      const h = containerRef.current ? containerRef.current.clientHeight : 0;
      if (h > 0 && h === prevHeight) {
        adjustContentScale();
        // 延遲覆核：首次開啟時父層的外部 CSS（<link> 標籤）可能尚未載入，
        // 導致初始測量的容器尺寸不正確。延遲再檢查一次以補捉 CSS 載入後的版面變化。
        recheckId = setTimeout(() => {
          if (cancelled || !contentRef.current || !containerRef.current) return;
          if (contentRef.current.scrollHeight > containerRef.current.clientHeight) {
            adjustContentScale();
          }
        }, 500);
      } else if (retries < MAX_RETRIES) {
        prevHeight = h;
        retries++;
        requestAnimationFrame(waitForStableLayout);
      } else {
        if (contentRef.current) contentRef.current.style.visibility = '';
      }
    };
    requestAnimationFrame(waitForStableLayout);

    // MutationObserver：只在內容實際溢出容器時才重新縮放
    const mutationObserver = new MutationObserver(() => {
      if (cancelled || isAdjustingRef.current) return;
      if (!contentRef.current || !containerRef.current) return;
      if (contentRef.current.scrollHeight <= containerRef.current.clientHeight) return;
      clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        if (cancelled) return;
        adjustContentScale();
      }, 80);
    });
    mutationObserver.observe(content, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => {
      cancelled = true;
      clearTimeout(debounceId);
      clearTimeout(recheckId);
      mutationObserver.disconnect();
      if (contentRef.current) contentRef.current.style.visibility = '';
    };
  }, [pagedHtml, currentPage, shouldSplited, splitType]);

  // 限制 .text 內 img 的最大高度，避免圖片超出容器
  useEffect(() => {
    if (!pagedHtml || !contentRef.current || !shouldSplited) return;
    const rafId = requestAnimationFrame(() => {
      const maxH = containerHeightRef.current > 0
        ? `${containerHeightRef.current}px`
        : '80vh';
      const textImgs = contentRef.current.querySelectorAll('.text img');
      textImgs.forEach(img => {
        // 已由 applyImgScale 接管的圖片不覆寫 maxHeight：兩邊都寫同一個屬性會互相
        // 蓋掉，而誰先誰後取決於各自 effect 的 deps 何時變化，結果不穩定（NBK-1671）。
        // 這裡仍保留給沒走縮放的型別，以及圖片延遲載入時的保險。
        if (img.dataset.fitScaled === '1') return;
        img.style.maxHeight = maxH;
        img.style.objectFit = 'contain';
      });
    });
    return () => cancelAnimationFrame(rafId);
  }, [pagedHtml, currentPage, shouldSplited]);

  // 確保即使不需要分頁，也要處理圖片超出畫面高度的情況
  useEffect(() => {
    if (!pagedHtml || !splitType || !contentRef.current || !containerRef.current) return;

    const isTextStructureType = extendedResourceStyle === LayoutType.PlainText || 
                                extendedResourceStyle === LayoutType.Topic_108_2;
    const shouldHandleImageResize = isTextStructureType && shouldSplited && splitType === SplitType.NO_SPLIT;

    if (!shouldHandleImageResize) return;

    const rafId = requestAnimationFrame(() => adjustImagesToFitPage(contentRef, 0));
    return () => cancelAnimationFrame(rafId);
  }, [pagedHtml, contentRef, containerRef, extendedResourceStyle, shouldSplited, splitType]);

  useEffect(() => {
    // 非 split 模式由 ExtendedContentContainer 的 <MathJax.Text> 負責 typeset；
    // 此處自管 typeset 只在 split 模式跑，避免同一份 DOM 被兩個 owner 重排（公式顯示兩次）。
    if (!shouldSplited) return;
    if (pagedHtml && contentRef.current) {
      if (window.MathJax && window.MathJax.Hub) {
        window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub, contentRef.current]);
      }
    }
  }, [pagedHtml, shouldSplited]);

  // 注意：這裡曾經有第二個「點答案／解析區切換 .show」的內嵌 handler
  // （b4d815b08 fix: fix answer btn #NBK-1438，1.28.3 線上獨立實作的版本）。
  // 它與上方 NBK-1663 的委派 handler 掛在同一個 contentRef 上，兩者都 toggle
  // 同一個 .show，一次點擊被切兩次、淨效果為零，功能整個失效。兩個 useEffect
  // 在檔案裡隔了數百行，git 合併時不會報衝突，所以是靜默壞掉。
  // 已移除內嵌版，委派版為唯一 owner —— 它涵蓋更多內容型別
  // （.wordAns / .check / .ansCheck / .ANSWERS / .fill .ans）、與 SynopsisWindow
  // 共用同一份邏輯，且會 clone-replace 剝掉 layout script 綁的 listener，
  // 不必依賴「script 的 listener 已隨 DOM 重建而失效」這個時序假設。
  // 若日後合併又把內嵌版帶回來，請刪掉它，不要兩個並存。

  return (
    // noScroll：layout CSS 以 `.noScroll .imageSet ...` 區分切頁模式，名稱不可改。
    // imageSetHost：圖片型一律隱藏卷軸（imageSet.scss），涵蓋此 div 與所有子孫
    <div ref={containerRef} className={classnames({ noScroll: shouldSplited, imageSetHost: isImageLayout })}>
      {pagedHtml && (
        <>
          <div
            ref={contentRef}
            style={{ height: '100%' }}
            dangerouslySetInnerHTML={{
              __html: pagedHtml,
            }}
          />
          {shouldSplited && splitType === SplitType.AUTO_SPLIT && (extendedResourceStyle === LayoutType.PlainText || 
                                                                    extendedResourceStyle === LayoutType.Topic_108_2 || 
                                                                    extendedResourceStyle === LayoutType.Image_108_2) && (
            <TextStructureHandler
              pagedHtml={pagedHtml}
              currentPage={currentPage}
              currentPageIndex={currentPage}
              contentRef={contentRef}
            />
          )}
          {shouldSplited && splitType === SplitType.AUTO_SPLIT && (extendedResourceStyle === LayoutType.Simple_108_2 || 
                                                                    extendedResourceStyle === LayoutType.Fill_108_2) && (
            <ContentStructureHandler
              pagedHtml={pagedHtml}
              currentPage={currentPage}
              currentPageIndex={currentPage}
              contentRef={contentRef}
            />
          )}
        </>
      )}
    </div>
  );
};

export default StaticHtmlComponent;
